<template>
  <Modal
    :name="$constants.dynamicModals.SIGN_IN_MODAL"
    :custom="true"
    :persistent="$route.path.includes('login-external')"
    @before-open="handleBeforeOpen"
  >
    <template #default="{ persistent, onClose }">
      <ModalBlack
        :persistent="persistent"
        class="sign-in-modal"
        :class="{ 'sign-in-modal--failed': !isValid }"
        @close="onClose"
      >
        <BravadoText tag="h2" variant="header3" class="sign-in-modal__title">Welcome Back!</BravadoText>

        <!-- SOCIAL SIGN-INS -->
        <section v-if="!isUserInsideExternalApp">
          <BravadoText tag="p" variant="body" class="sign-in-modal__body">
            Sign in to Bravado using one of your social media accounts
          </BravadoText>

          <SignUpSocialButtons
            class="sign-in-modal__social-buttons"
            type="login"
            :login-params="params"
            @login="onSocialLogin"
          />
          <BravadoText tag="p" variant="caption1" class="sign-in-modal__divider">OR</BravadoText>
        </section>

        <form class="sign-in-modal__form" @submit.prevent="handleLogin">
          <BravadoText v-if="isUserInsideExternalApp" tag="p" variant="body" class="sign-in-modal__body">
            Sign in to Bravado
          </BravadoText>
          <BravadoTextInputV3
            v-model.trim="form.email"
            :is-valid="isFieldValid('email')"
            :hint="emailErrorMessage"
            name="email"
            placeholder="Personal email"
            data-test="email-input"
            @blur="$v.form.email.$touch"
          >
            <template slot="icon">
              <AtIcon width="20" height="20" />
            </template>
          </BravadoTextInputV3>

          <div class="sign-in-modal__field">
            <BravadoTextInputV3
              ref="passwordInput"
              v-model="form.password"
              :is-valid="isFieldValid('password')"
              :hint="passwordErrorMessage"
              type="password"
              name="password"
              :input-style="{ paddingRight: '60px' }"
              placeholder="Password"
              data-test="password-input"
              @blur="$v.form.password.$touch"
            >
              <template slot="icon">
                <LockIcon width="20" height="20" />
              </template>
              <template slot="append">
                <NuxtLink
                  :to="{ query: { dynamic_modal: $constants.dynamicModals.RESTORE_PASSWORD_MODAL } }"
                  class="sign-in-modal__forgot"
                >
                  Forgot password?
                </NuxtLink>
              </template>
            </BravadoTextInputV3>
          </div>

          <div class="sign-in-modal__field">
            <BravadoButtonV3 type="submit" variant="primary" class="sign-in-modal__submit" data-test="login-button">
              Sign In
            </BravadoButtonV3>
          </div>
        </form>

        <BravadoText class="sign-in-modal__hint" tag="p" variant="body">
          Not a member?
          <NuxtLink :to="linkToSignUp" class="sign-in-modal__link">Sign Up</NuxtLink>
        </BravadoText>
      </ModalBlack>
    </template>
  </Modal>
</template>

<script lang="ts">
import Vue from 'vue';
import { validationMixin } from 'vuelidate';
import { email, minLength, required } from 'vuelidate/lib/validators';
import type { Location } from 'vue-router';

import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import Modal from '~/components/common/Modal/Modal.vue';
import { getValidReferrer } from '~/components/common/SignUpModal/utils';
import type { ModalEmitedEvent } from '~/plugins/modal/types';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import ModalBlack from '~/components/common/SignUpModal/ModalBlack/ModalBlack.vue';
import SignUpSocialButtons from '~/components/common/SignUpModal/SignUpSocialButtons/SignUpSocialButtons.vue';
import { isUserInsideExternalApp } from '~/utils/externalRedirections';
import AtIcon from '~/assets/svg/u_at.vue.svg';
import LockIcon from '~/assets/svg/u_lock.vue.svg';

export default Vue.extend({
  name: 'SignInModal',
  components: {
    Modal,
    BravadoTextInputV3,
    AtIcon,
    LockIcon,
    SignUpSocialButtons,
    BravadoButtonV3,
    ModalBlack,
  },
  mixins: [validationMixin],
  data() {
    return {
      form: {
        email: '',
        password: '',
      },
      isUserInsideExternalApp: false,
      isValid: true,
      params: {},
      attemptedSubmission: false,
      loading: false,
    };
  },
  validations: {
    form: {
      email: { required, email },
      password: { required, minLength: minLength(6) },
    },
  },
  computed: {
    emailErrorMessage(): string {
      if (this.isFieldValid('email')) return '';
      if (!this.$v.form.email?.required) return 'Email is required';
      if (!this.$v.form.email.email) return 'Please provide a valid email';

      return '';
    },
    passwordErrorMessage(): string {
      if (this.isFieldValid('password')) return '';
      if (!this.$v.form.password?.required) return 'Password is required';
      if (!this.$v.form.password.minLength) return 'Password must be at least 6 characters long';

      return '';
    },
    linkToSignUp(): Location {
      const query = { ...this.$route.query };
      delete query.onboarding;
      return { query: { ...query, dynamic_modal: this.$constants.dynamicModals.WAR_ROOM_SIGN_UP_MODAL } };
    },
    // Fix typo
    referer(): string {
      const { query } = this.$route;
      return (query.referer || query.refferer) as string;
    },
    modalShowing() {
      return this.$route.query.dynamic_modal;
    },
  },
  watch: {
    'form.email'(value: string) {
      if (!value) return;
      try {
        sessionStorage.setItem('email', value?.trim());
      } catch (e) {
        this.$sentryUtils.error(e);
      }
    },
    modalShowing: {
      handler() {
        this.setIsUserInsideExternalApp();
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.$route.query.dynamic_modal !== 'sign-in') {
      this.$modal.hide(this.$constants.dynamicModals.SIGN_IN_MODAL);
      return;
    }

    this.setIsUserInsideExternalApp();

    this.restoreEmail();
  },
  methods: {
    handleBeforeOpen({ params }: ModalEmitedEvent) {
      this.restoreEmail();
      const { path } = this.$route;
      const { tab, dynamic_modal, ...rest } = params;
      this.params = {
        ...rest,
        refferer: rest.referer || rest.refferer || `${process.env.internalBaseUrl}${path}`,
      };

      if (tab === 'sign-up') {
        this.$router.replace({
          query: { dynamic_modal: 'sign-up', ...rest },
        });
      }
    },
    isFieldValid(fieldName: string) {
      const formElement = this.$v.form[fieldName]!;
      return !(this.attemptedSubmission && formElement.$invalid) && this.isValid;
    },
    async handleLogin() {
      if (this.loading) return;
      if (this.$v.form.$invalid) {
        this.attemptedSubmission = true;
      } else {
        this.loading = true;

        try {
          await this.$auth.login({
            data: {
              user: {
                email: this.form.email,
                password: this.form.password,
              },
            },
          });

          if (getValidReferrer(this.referer)) {
            window.location.href = `${this.referer}?${new URLSearchParams(this.$route.query as {}).toString()}`;
          }
        } catch (e) {
          // @ts-expect-error - error
          if (e.response?.data?.error) {
            // Devise uses the same error message as for confirmation, but we don't have that flow
            const DEACTIVATION_ERROR = 'Your account is locked.';
            // @ts-expect-error - error
            if (e.response.data.error === DEACTIVATION_ERROR) {
              this.$router.push({
                query: { error: 'deactivated' },
              });
            } else {
              // @ts-expect-error - error
              this.$notice.error(e.response.data.error);
            }
          }
          this.isValid = false;

          setTimeout(() => {
            this.isValid = true;
          }, 3000);
        } finally {
          this.loading = false;
        }
      }
    },

    onSocialLogin(provider: 'google' | 'linkedin') {
      this.$analytics.logEvent(`global__sign-in-modal__${provider}-button__click`);
    },

    // Restore email from prev forms
    restoreEmail() {
      try {
        const email = sessionStorage.getItem('email');
        if (email) {
          this.form.email = email;

          // Component could be notmounted yet + we should rewrite parent's autofocus
          // $nextTick is too short here
          setTimeout(() => {
            // @ts-ignore
            this.$refs.passwordInput?.focus();
          }, 100);
        }
      } catch (e) {
        this.$sentryUtils.error(e);
      }
    },
    setIsUserInsideExternalApp() {
      if (process.server) return;
      this.isUserInsideExternalApp = isUserInsideExternalApp(
        document.referrer,
        navigator.userAgent,
        this.$device.isWebView,
      );
    },
  },
});
</script>

<style scoped lang="scss">
@import '~/assets/styles/uikit/utils/transition.scss';

.sign-in-modal {
  &--failed {
    @extend %shake-transition;
  }

  &__close {
    position: absolute;
    top: 17px;
    right: 16px;
    cursor: pointer;
    z-index: 2;
    color: var(--black-a30);

    &:hover {
      color: rgba(var(--black));
    }
  }

  &__submit {
    width: 100%;
  }

  &__title,
  &__body {
    margin: 16px auto;
  }

  &__divider {
    margin: 12px 0;
    font-weight: 600;
    color: var(--gray-50);
  }

  &__title {
    font-size: 28px;
    font-weight: 600;
    line-height: 1.27272727;
    letter-spacing: -0.5px;
    margin-top: 0;
  }

  &__body {
    text-align: center;
  }

  &__footer {
    margin: 18px auto 0;
  }

  &__form {
    text-align: left;

    .sign-in-modal__body {
      margin-bottom: 20px;
    }
  }

  &__field {
    position: relative;
    margin-top: 12px;
  }

  &__hint {
    margin-top: 20px;
    color: var(--gray-50);
  }

  &__link {
    font-size: inherit;
    font-weight: inherit;
    font-family: inherit;
    color: var(--blue-60);
    text-decoration: none;
  }

  &__forgot {
    @include caption1;

    color: var(--gray-50);
    padding: 8px;
    text-decoration: none;
    display: inline-block;
  }

  ::v-deep .bravado-text-input-v3__append {
    right: 4px;
  }

  ::v-deep .bravado-text-input-v3__icon {
    color: var(--gray-50);
  }
}
</style>
